import React from "react";
import { Box, Link } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { colors, fonts, gradients, layout } from "../theme/tokens";

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
  { label: "Services", href: "/services" },
  { label: "Contact Us", href: "/contact-us" },
];

const serviceLinks = [
  { label: "Strategy & Management", href: "/services#strategy-management" },
  { label: "Corporate Finance", href: "/services#corporate-finance" },
  { label: "Risk & Compliance", href: "/services#risk-compliance" },
  { label: "Technology & Digital", href: "/services#it" },
  { label: "Property Consultancy", href: "/services#property" },
];

const columnLabelSx = {
  fontFamily: fonts.display,
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: ".16em",
  color: colors.amberDot,
  mb: "18px",
};

const footerLinkSx = {
  fontFamily: fonts.body,
  fontSize: "14.5px",
  color: colors.footerText,
  transition: "color .2s ease",
  "&:hover": { color: "#fff" },
};

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        background: gradients.footer,
        color: "#fff",
        px: { xs: "20px", md: "28px" },
        pt: "64px",
        pb: "30px",
      }}
    >
      <Box sx={{ maxWidth: layout.maxWidth, mx: "auto" }}>
        {/* Top grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              md: "1.5fr 1fr 1fr 1.2fr",
            },
            gap: { xs: "32px 24px", md: "40px" },
            pb: "48px",
            borderBottom: "1px solid rgba(255,255,255,.1)",
          }}
        >
          {/* Brand */}
          <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
              <Box
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "26px",
                  color: "#fff",
                  letterSpacing: ".02em",
                }}
              >
                ARGYLE
              </Box>
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: colors.amberDot,
                  ml: "2px",
                }}
              />
            </Box>
            <Box
              sx={{
                fontFamily: fonts.display,
                fontSize: "12px",
                letterSpacing: ".32em",
                color: colors.footerLabel,
                mt: "2px",
              }}
            >
              CONSULTING LTD
            </Box>
            <Box
              sx={{
                fontFamily: fonts.body,
                fontSize: "14px",
                lineHeight: 1.65,
                color: colors.footerMuted,
                mt: "18px",
                maxWidth: "300px",
              }}
            >
              A professional consulting firm providing strategy, corporate
              finance, advisory, human resource, risk & compliance, audit,
              technology, and property services to companies in Nigeria and
              beyond.
            </Box>
          </Box>

          {/* Quick links */}
          <Box>
            <Box sx={columnLabelSx}>QUICK LINKS</Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "11px" }}>
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  underline="none"
                  sx={footerLinkSx}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>

          {/* Services */}
          <Box>
            <Box sx={columnLabelSx}>SERVICES</Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "11px" }}>
              {serviceLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  underline="none"
                  sx={footerLinkSx}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>

          {/* Get in touch */}
          <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
            <Box sx={columnLabelSx}>GET IN TOUCH</Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "13px" }}>
              <Link
                href="tel:+2348033910386"
                underline="none"
                sx={{
                  ...footerLinkSx,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <CallIcon sx={{ fontSize: "16px", color: colors.amberDot }} />
                +234 803 391 0386
              </Link>
              <Link
                href="mailto:info@argyleconsulting.org"
                underline="none"
                sx={{
                  ...footerLinkSx,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <EmailIcon sx={{ fontSize: "16px", color: colors.amberDot }} />
                info@argyleconsulting.org
              </Link>
              <Box
                sx={{
                  fontFamily: fonts.body,
                  fontSize: "14.5px",
                  color: colors.footerText,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <LocationOnIcon
                  sx={{ fontSize: "16px", color: colors.amberDot }}
                />
                Lagos State, Nigeria
              </Box>
            </Box>
            <Link
              href="/contact-us"
              underline="none"
              sx={{
                display: "inline-block",
                mt: "20px",
                background: gradients.amber,
                color: "#fff",
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: "13.5px",
                px: "22px",
                py: "11px",
                borderRadius: "40px",
              }}
            >
              Get In Touch
            </Link>
          </Box>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "14px",
            pt: "24px",
          }}
        >
          <Box
            sx={{
              fontFamily: fonts.body,
              fontSize: "13px",
              color: colors.footerCopy,
            }}
          >
            © {new Date().getFullYear()} Argyle Consulting Ltd. All rights
            reserved.
          </Box>
          <Box sx={{ display: "flex", gap: "22px" }}>
            <Link
              href="#"
              underline="none"
              sx={{
                fontFamily: fonts.body,
                fontSize: "13px",
                color: colors.footerCopy,
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="#"
              underline="none"
              sx={{
                fontFamily: fonts.body,
                fontSize: "13px",
                color: colors.footerCopy,
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
